import { useState } from "react";
import { Bar } from "react-chartjs-2";
import { useGetWordExportsFullQuery } from "../../services/API/statisticsAPI";
import exportAllExports from "./Export/exportAllExports";
import { useTranslation } from "react-i18next";
import LegendToggleRow from "./LegendToggleRow";
import ChartCard from "./ChartCard";
import CsvDownloadButton from "./CsvDownloadButton";
import { baseOptions, colorAt, monthlyScales, YEAR_COLORS } from "./chartTheme";

const OPTION_KEYS = [
  "optionConstructionSiteSetup",
  "optionMicropiles",
  "optionPileheads",
  "optionTestLoads",
  "optionRequirementItems",
];

const WordExportStats = () => {
  const { t } = useTranslation(["translation", "statistics"]);
  const todayYear = new Date().getFullYear();
  const [hiddenYears, setHiddenYears] = useState([]);

  const toggleYear = (year) =>
    setHiddenYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );

  const { data: wordExportsFullData } = useGetWordExportsFullQuery(undefined, {
    pollingInterval: 60000,
  });

  // Derive all years present in data
  const availableYears = Array.isArray(wordExportsFullData)
    ? [
        ...new Set(
          wordExportsFullData
            .filter((item) => item?.exportAt)
            .map((item) => new Date(item.exportAt).getFullYear())
        ),
      ].sort((a, b) => a - b)
    : [todayYear];

  // Aggregate per year+month per option: { [year]: { [month 0-11]: { [optionKey]: count } } }
  const counts = {};
  availableYears.forEach((y) => {
    counts[y] = Array.from({ length: 12 }, () =>
      Object.fromEntries(OPTION_KEYS.map((k) => [k, 0]))
    );
  });

  if (Array.isArray(wordExportsFullData)) {
    wordExportsFullData.forEach((item) => {
      if (item?.exportAt) {
        const d = new Date(item.exportAt);
        const y = d.getFullYear();
        const m = d.getMonth();
        if (y in counts) {
          (item.selectedOptions ?? []).forEach((opt) => {
            if (opt in counts[y][m]) counts[y][m][opt]++;
          });
        }
      }
    });
  }

  // Year filter: the x-axis only contains months of years that aren't
  // toggled off; the year suffix is dropped when a single year is visible
  const visibleYears = availableYears.filter((year) => !hiddenYears.includes(year));

  const xLabels = visibleYears.flatMap((y) =>
    Array.from(
      { length: 12 },
      (_, m) =>
        new Date(y, m).toLocaleString("default", { month: "short" }) +
        (visibleYears.length > 1 ? ` ${y}` : "")
    )
  );

  const monthlyChartData = {
    labels: xLabels,
    datasets: OPTION_KEYS.map((key, i) => ({
      label: t(`createlv.${key}`),
      data: visibleYears.flatMap((y) => Array.from({ length: 12 }, (_, m) => counts[y][m][key])),
      backgroundColor: colorAt(i),
      borderWidth: 0,
      maxBarThickness: 28,
      stack: "Stack 0",
    })),
  };

  const base = baseOptions();
  const scales = monthlyScales();
  const monthlyChartOptions = {
    ...base,
    interaction: { mode: "index", intersect: false },
    scales: {
      x: { ...scales.x, stacked: true },
      y: { ...scales.y, stacked: true },
    },
  };

  return (
    <ChartCard
      title={t("wordexport.title", { ns: "statistics" })}
      subtitle={t("wordexport.clicks_per_month", { ns: "statistics" })}
      action={
        <CsvDownloadButton
          data={exportAllExports.wordExportFull(wordExportsFullData ?? [])}
          headers={exportAllExports.wordExportHeadersFull()}
          filename={"stats_word_exports.csv"}
        />
      }
      footer={
        <LegendToggleRow
          items={availableYears.map((year) => ({
            key: year,
            label: year.toString(),
            color:
              year === Math.max(...availableYears) ? YEAR_COLORS.current : YEAR_COLORS.previous,
            active: !hiddenYears.includes(year),
          }))}
          onToggle={toggleYear}
        />
      }
    >
      <Bar options={monthlyChartOptions} data={monthlyChartData} />
    </ChartCard>
  );
};

export default WordExportStats;
